import { useEffect, useState } from 'react';
import type { StudentDashboard } from '@starter/shared';
import { api, ApiError } from '../../api/client.js';
import { ErrorBanner } from '../../components/ErrorBanner.js';
import { LoadingState } from '../../components/LoadingState.js';
import { PageHeader } from '../../components/PageHeader.js';
import { StatusBadge } from '../../components/StatusBadge.js';

export function StudentDashboardPage() {
  const [data, setData] = useState<StudentDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    api
      .studentDashboard()
      .then(setData)
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Failed to load dashboard'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  if (loading) return <LoadingState message="Loading your dashboard…" />;
  if (error) return <ErrorBanner message={error} onRetry={load} />;
  if (!data) return null;

  return (
    <div className="page">
      <PageHeader
        title="Dashboard"
        subtitle={`${data.programName} · ${data.organizationName}`}
      />

      <div className="stat-grid">
        <div className="card stat-card">
          <span className="stat-card__label">Cohort</span>
          <span className="stat-card__value">{data.cohortName ?? 'Not assigned'}</span>
          {data.cohortStatus && <StatusBadge status={data.cohortStatus} />}
        </div>
        <div className="card stat-card">
          <span className="stat-card__label">Enrollment</span>
          <span className="stat-card__value">
            {data.enrollmentStatus ? data.enrollmentStatus.replace(/_/g, ' ') : '—'}
          </span>
        </div>
        <div className="card stat-card">
          <span className="stat-card__label">Student ID</span>
          <span className="stat-card__value">{data.studentIdNumber ?? '—'}</span>
        </div>
      </div>

      {(data.cohortStartDate || data.cohortEndDate) && (
        <section className="card">
          <h2 className="card__title">Cohort timeline</h2>
          <dl className="detail-list">
            {data.cohortStartDate && (
              <>
                <dt>Start</dt>
                <dd>{data.cohortStartDate}</dd>
              </>
            )}
            {data.cohortEndDate && (
              <>
                <dt>End</dt>
                <dd>{data.cohortEndDate}</dd>
              </>
            )}
          </dl>
        </section>
      )}
    </div>
  );
}
